/**
 * Workflows P5-4: the conversation-control steps (collect a visitor reply, lock / unlock the composer, snooze, set the
 * language) and the reply-button options. Merged into `engage.workflows` (steps, summaries, exits, incomplete, fields,
 * problems, channel notes, Add-step keywords, canvas).
 */
export const workflowsConversation = {
  steps: {
    collect_reply: "Collect customer reply",
    disable_reply: "Disable customer reply",
    enable_reply: "Enable customer reply",
    snooze: "Snooze conversation",
    set_language: "Set language",
  },
  summaries: {
    status_closed: "Close conversation",
    status_resolved: "Resolve conversation",
    collect_reply: "{{question}} (saved to {{target}})",
    disable_reply: "The visitor can only use the buttons and cards that follow",
    enable_reply: "The visitor can type again",
    snooze_minutes_one: "Snooze for {{count}} minute",
    snooze_minutes_other: "Snooze for {{count}} minutes",
    snooze_hours_one: "Snooze for {{count}} hour",
    snooze_hours_other: "Snooze for {{count}} hours",
    snooze_days_one: "Snooze for {{count}} day",
    snooze_days_other: "Snooze for {{count}} days",
    snooze_until: "Snooze until {{date}} at {{time}}",
    snooze_office: "Snooze until the office opens",
    set_language: "Language: {{language}}",
  },
  exits: {
    replied: "Replied",
    noReply: "No reply in time",
    woke: "When it wakes",
    startOver: "Start over",
  },
  incomplete: {
    reply_range: "The shortest answer can't be longer than the longest",
    reply_format: "Pick the format that fits where the answer is saved",
    snoozeUntil: "Choose the date and time to snooze until",
  },
  fields: {
    replyQuestionHint: "The visitor answers in the chat box. Their answer never reaches the assistant.",
    replyFormat: "Format of the answer",
    replyFormatHint: "A bad answer is asked for once more; after that the assistant takes over.",
    replyFormats: { any: "Any text", email: "Email address", phone: "Phone number", number: "Number", digits: "Digits only" },
    replyFormatProblem: "The answer must be an email address or a phone number to be saved there.",
    replyLength: "Length of the answer",
    replyMin: "Shortest answer",
    replyMax: "Longest answer",
    replyCharacters: "characters",
    replyLengthHint: "From 1 to {{max}} characters.",
    replyRangeProblem: "The shortest answer can't be longer than the longest.",
    repliedGoTo: "After the answer",
    repliedHint: "Choose a path, or carry on with the next step.",
    noReplyAfter: "Stop waiting after a while",
    noReplyAfterHint: "If nobody answers in time, the workflow takes another path or ends.",
    noReplyUnattached: "This trigger starts before a conversation exists, so a reply cannot time out.",
    noReplyWait: "Wait for the answer for",
    noReplyGoTo: "If nobody answers",
    noReplyHint: "Where the workflow goes when the time runs out.",
    snoozeIntro: "The conversation leaves the open list and waits in the Inbox's Snoozed view. A reply from the visitor or a teammate, or the conversation closing, wakes it early.",
    snoozeMode: "Snooze",
    snoozeModes: { for: "For a while", until: "Until a time", until_office_hours: "Office hours" },
    snoozeFor: "Snooze for",
    snoozeUntil: "Snooze until",
    snoozeDate: "Date",
    snoozeTime: "Time",
    snoozeZone: "In your workspace time zone ({{zone}}). A moment that has passed is skipped.",
    snoozeOfficeHint: "Snoozes until the agent's office hours open next. If the office is open, or has no hours, the step is skipped.",
    wokeGoTo: "When it wakes",
    wokeHint: "Choose a path, or carry on with the next step.",
    snoozeInterruptedGoTo: "If someone replies first",
    snoozeInterruptedHint: "A reply from the visitor or a teammate, or the conversation closing. Where the workflow goes then.",
    languageChoice: "Language",
    languageHint: "Sets the language of the conversation.",
    languages: { en: "English", fil: "Filipino", auto: "Detect automatically" },
    languageEffect: "The assistant answers in this language and the lines Sagot writes follow it. Automatic detection will not change it. “Detect automatically” gives the choice back.",
    lockHelp: "Locks the chat box: the visitor can only use the buttons and cards that follow.",
    unlockHelp: "Lets the visitor type in the chat box again.",
    lockLifts: "The lock lifts by itself at a Collect customer reply or Let the assistant answer step, at a handover, and when the workflow ends.",
    lockIdle: "Nothing the visitor answers follows this lock, so it changes nothing yet.",
    startOver: "Add a Start over button",
    startOverHint: "A last button that sends the visitor back to Path A. It counts toward the 8 buttons.",
    startOverFirstPath: "Start over goes back to Path A, so it can't be added in Path A.",
    startOverFull: "All {{max}} buttons are used. Remove one to add Start over.",
    startOverGoes: "Start over goes to Path {{path}}. Its label can be translated in the Translations tab.",
    saveChoice: "Remember the choice",
    saveChoiceHint: "Saves the label of the button the visitor chose, so later steps and conditions can use it.",
    saveChoiceTo: "Save the chosen button to",
  },
  builder: {
    channelNotes: {
      steps: {
        collect_reply: {
          web: "Web chat, web app and mobile: the chat box takes the answer (it is not a card). Its keyboard fits the format.",
          messenger: "Messenger: a plain text question; the next message is the answer.",
          email: "Email: the question is in the reply; the answer is read above the quoted text.",
          api: "Visitor-facing steps do not run on the developer API channel.",
        },
        disable_reply: {
          web: "Web chat, web app and mobile disable the composer.",
          other: "Messenger, email and API cannot hide it; a typed message is answered as typing on a card that does not allow typing: asked again once, then the assistant answers.",
        },
        enable_reply: {
          web: "Web chat, web app and mobile enable the composer again.",
          other: "Messenger, email and API never hide the composer, so nothing changes there.",
        },
        snooze: { behind: "Works behind the scenes: the visitor sees nothing, on every channel." },
        set_language: { behind: "Works behind the scenes on every channel. The assistant's next answer and the translated texts follow the language." },
      },
    },
  },
  connectors: {
    fixedExit: "Fixed: always goes to Path A",
  },
} as const;
